import React from 'react'

/**
 * 데이터셋 화면 전용 표시 포맷터.
 * 상태에 의존하지 않는 순수 함수만 둔다 (페이지에서 분리해 재사용·테스트 용이).
 */
export function formatBytes(n?: number): string {
  if (!n || n <= 0) return '-'
  const units = ['B','KB','MB','GB','TB']
  let idx = 0
  let val = n
  while (val >= 1024 && idx < units.length - 1) { val /= 1024; idx++ }
  return `${val.toFixed(1)} ${units[idx]}`
}

export function renderSampling(s: any): string {
  if (!s) return '-'
  const method = s.method || 'random'
  const splits = s.splits ? `train:${s.splits.train}% val:${s.splits.val}% test:${s.splits.test}%` : ''
  const seed = typeof s.seed !== 'undefined' ? `, seed:${s.seed}` : ''
  return `${method}(${splits}${seed})`
}

export function renderOutput(o: any): string {
  if (!o) return '-'
  const task = o.task || '-'
  const fmt = o.format || '-'
  return `${task} / ${fmt}`
}

export function formatLocalDate(value?: string | number | Date): string {
  if (!value) return '-'
  try {
    if (typeof value === 'string') {
      const s = value.trim()
      // Z(UTC) 또는 명시적 오프셋(+09:00 등)이 없으면 UTC로 간주하여 Z를 부여
      const hasTZ = /[zZ]|([+-]\d{2}:?\d{2})$/.test(s)
      let normalized = s
      if (!hasTZ) {
        normalized = s.includes('T') ? `${s}Z` : `${s.replace(' ', 'T')}Z`
      }
      const d = new Date(normalized)
      if (!Number.isNaN(d.getTime())) return d.toLocaleString()
    }
    const d = new Date(value as any)
    if (!Number.isNaN(d.getTime())) return d.toLocaleString()
    return String(value)
  } catch {
    return String(value)
  }
}

export function renderSources(srcs?: Array<{ node_id: number; node_name?: string; node_path?: string; batch_id?: number; batch_filename?: string }>): string {
  if (!srcs || srcs.length === 0) return '-'
  return srcs.map(s => {
    const node = s.node_path ?? s.node_name ?? `node#${s.node_id}`
    const batch = s.batch_filename ? ` > ${s.batch_filename}` : ''
    return `${node}${batch}`
  }).join(', ')
}

export function computeSplitPercents(total: number, train: number, val: number, test: number): { trainPct: number; valPct: number; testPct: number } {
  if (!total || total <= 0) return { trainPct: 0, valPct: 0, testPct: 0 }
  const clamp = (n: number) => Math.max(0, n)
  const t = clamp(train), v = clamp(val), te = clamp(test)
  const sum = t + v + te || 1
  // 비율이 합쳐서 100%가 되도록 정규화
  return {
    trainPct: Math.round((t / sum) * 1000) / 10,
    valPct: Math.round((v / sum) * 1000) / 10,
    testPct: Math.round((te / sum) * 1000) / 10,
  }
}

export function renderSourcePills(srcs?: Array<{ node_id: number; node_name?: string; node_path?: string; batch_id?: number; batch_filename?: string }>): React.ReactNode {
  if (!srcs || srcs.length === 0) return <span className="text-gray-500">-</span>
  const shown = srcs.slice(0, 3)
  const rest = srcs.length - shown.length
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((s, idx) => (
        <span key={`${s.node_id}-${idx}`} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
          {(s.node_path ?? s.node_name ?? `node#${s.node_id}`)}{s.batch_filename ? ` > ${s.batch_filename}` : ''}
        </span>
      ))}
      {rest > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">+{rest} more</span>
      )}
    </div>
  )
}
